import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Grid,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import { useDistrictCache } from '../../hooks/useDistrictCache';

// Individual metric card for horizontal layout
const MetricCard = ({ title, subtitle, trend, metrics, onClick }) => {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp sx={{ color: 'success.main', fontSize: '1.4rem' }} />;
      case 'down':
        return <TrendingDown sx={{ color: 'error.main', fontSize: '1.4rem' }} />;
      default:
        return <TrendingFlat sx={{ color: 'action.active', fontSize: '1.4rem' }} />;
    }
  };

  const getMetricColor = (index, totalMetrics) => {
    if (index === 0) return 'success.main';
    if (index === totalMetrics - 1) return 'error.main';
    return 'text.secondary';
  };

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: 'primary.lighter',
          borderColor: 'primary.light',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: 'translateY(-2px)',
        }
      }}
    >
      {/* Header with trend icon and title */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        {getTrendIcon()}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'text.primary',
              lineHeight: 1.3
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.8rem',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Metrics row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        {metrics.map((metric, index) => (
          <Box key={index} sx={{ textAlign: 'center', flex: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.75rem',
                mb: 0.5
              }}
            >
              {metric.label}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700,
                color: getMetricColor(index, metrics.length),
                fontSize: '1.1rem'
              }}
            >
              {metric.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const PortfolioMetricsSummary = () => {
  const navigate = useNavigate();
  const { districtData, loading, error } = useDistrictCache({ silentLoad: true });

  // Filter schools with valid metric data
  const validSchools = useMemo(() => {
    if (!districtData?.metrics) return [];
    return districtData.metrics.filter(
      school => school?.population && school?.enrollment && school?.market_share
    );
  }, [districtData]);

  // Calculate metric counts - same logic as DistrictAnalysis
  const calculateMetricCounts = (schools, type, statusField = 'status') => {
    const total = schools.length;
    
    if (type !== 'market_share' && !(type === 'enrollment' && statusField === 'status')) {
      const growing = schools.filter(s => s[type][statusField] === 'growing').length;
      const declining = schools.filter(s => s[type][statusField] === 'declining').length;
      const stable = total - growing - declining;
      
      return {
        total,
        growing,
        declining,
        stable,
        newer: 0,
        growingPercent: total > 0 ? (growing / total) * 100 : 0,
        decliningPercent: total > 0 ? (declining / total) * 100 : 0,
        stablePercent: total > 0 ? (stable / total) * 100 : 0,
        newerPercent: 0
      };
    }
    
    const newer = schools.filter(s => s.is_newer).length;
    const newerPercent = (newer / total) * 100;
    const nonNewerSchools = schools.filter(s => !s.is_newer);
    
    if (type === 'market_share') {
      const gaining = nonNewerSchools.filter(s => s[type][statusField] === 'gaining').length;
      const losing = nonNewerSchools.filter(s => s[type][statusField] === 'losing').length;
      const stable = nonNewerSchools.length - gaining - losing;
      
      return {
        total,
        gaining,
        losing,
        stable,
        newer,
        gainingPercent: total > 0 ? (gaining / total) * 100 : 0,
        losingPercent: total > 0 ? (losing / total) * 100 : 0,
        stablePercent: total > 0 ? (stable / total) * 100 : 0,
        growing: gaining,
        declining: losing,
        growingPercent: total > 0 ? (gaining / total) * 100 : 0,
        decliningPercent: total > 0 ? (losing / total) * 100 : 0,
        newerPercent
      };
    } else {
      const growing = nonNewerSchools.filter(s => s[type][statusField] === 'growing').length;
      const declining = nonNewerSchools.filter(s => s[type][statusField] === 'declining').length;
      const stable = nonNewerSchools.length - growing - declining;
      
      return {
        total,
        growing,
        declining,
        stable,
        newer,
        growingPercent: total > 0 ? (growing / total) * 100 : 0,
        decliningPercent: total > 0 ? (declining / total) * 100 : 0,
        stablePercent: total > 0 ? (stable / total) * 100 : 0,
        newerPercent
      };
    }
  };

  // Build headlines data
  const headlines = useMemo(() => {
    const formatPercentage = (value) => {
      if (typeof value === 'number') {
        return `${value.toFixed(0)}%`;
      }
      return '0%';
    };

    const emptyMetrics = {
      total: 0, growing: 0, declining: 0, stable: 0, newer: 0,
      growingPercent: 0, decliningPercent: 0, stablePercent: 0, newerPercent: 0,
      gaining: 0, losing: 0, gainingPercent: 0, losingPercent: 0
    };

    const populationMetrics = validSchools.length 
      ? calculateMetricCounts(validSchools, 'population')
      : emptyMetrics;
      
    const enrollmentTrendMetrics = validSchools.length 
      ? calculateMetricCounts(validSchools, 'enrollment')
      : emptyMetrics;
      
    const projectedEnrollmentMetrics = validSchools.length 
      ? calculateMetricCounts(validSchools, 'enrollment', 'projection_status')
      : emptyMetrics;

    const getTrend = (metrics) => {
      return metrics.growing > metrics.declining ? 'up' : 
             metrics.declining > metrics.growing ? 'down' : 'neutral';
    };

    return [
      {
        title: 'Area Population Trends',
        subtitle: 'Since 2020',
        trend: getTrend(populationMetrics),
        metrics: [
          { label: 'Growing', value: formatPercentage(populationMetrics.growingPercent) },
          { label: 'Stable', value: formatPercentage(populationMetrics.stablePercent) },
          { label: 'Declining', value: formatPercentage(populationMetrics.decliningPercent) }
        ]
      },
      {
        title: 'School Enrollment Trends',
        subtitle: 'Since 2019',
        trend: getTrend(enrollmentTrendMetrics),
        metrics: [
          { label: 'Growing', value: formatPercentage(enrollmentTrendMetrics.growingPercent) },
          { label: 'Stable', value: formatPercentage(enrollmentTrendMetrics.stablePercent) },
          { label: 'Declining', value: formatPercentage(enrollmentTrendMetrics.decliningPercent) }
        ]
      },
      {
        title: 'School Enrollment Projections',
        subtitle: '2024 through 2029',
        trend: getTrend(projectedEnrollmentMetrics),
        metrics: [
          { label: 'Growing', value: formatPercentage(projectedEnrollmentMetrics.growingPercent) },
          { label: 'Stable', value: formatPercentage(projectedEnrollmentMetrics.stablePercent) },
          { label: 'Declining', value: formatPercentage(projectedEnrollmentMetrics.decliningPercent) }
        ]
      }
    ];
  }, [validSchools]);

  const handleNavigate = () => {
    navigate('/district-analysis');
  };

  // Loading state - show skeleton cards
  if (loading && !districtData) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" height={18} />
                    <Skeleton variant="text" width="50%" height={14} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                  <Skeleton variant="text" width={35} height={24} />
                  <Skeleton variant="text" width={35} height={24} />
                  <Skeleton variant="text" width={35} height={24} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error state or no data - show nothing
  if (error || !validSchools.length) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* All 3 metrics in one row */}
      <Grid container spacing={2}>
        {headlines.map((headline, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MetricCard
              title={headline.title}
              subtitle={headline.subtitle}
              trend={headline.trend}
              metrics={headline.metrics}
              onClick={handleNavigate}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PortfolioMetricsSummary;

